import React, { Component } from 'react';
import { withStyles } from '@quillbot/design-system';
import { Typography, Button, Grid, Theme } from '@quillbot/design-system';
import TrackSupport from 'src/support/handlers/track';
import { connect } from 'react-redux';
import { RootState, Common } from 'src/_common/models/redux/store';
import { User } from 'src/authentication/models/store';
import { getConfig } from 'next';
const { publicRuntimeConfig } = getConfig();

const styles = (theme: Theme) => ({
  root: {
    '&:hover': {
      backgroundColor: 'inherit',
      color: 'black',
    },
  },

  feedbackBtn: {
    marginRight: '5px',
  },
  textArea: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    color: theme.palette.text.primary,
    '&:focus-visible': {
      outline: 'none',
      borderColor: theme.palette.divider,
    },
  },
  textAreaError: {
    border: `1px solid ${theme.palette.error.main}`,
    '&::placeholder': {
      color: theme.palette.error.main,
    },
    '&:focus-visible': {
      borderColor: theme.palette.error.main,
    },
  },
});

type IState = {
  feedbackSubmitted: boolean;
  startedWriting: boolean;
  showError: boolean;
};

// TODO: fix: interface can't extend a type
// interface IProps extends WithStyles {
interface IProps {
  user: User;
  common: Common;
  // TODO: define proper type
  classes: any;
}

// declare global {
//   interface Window {
//     exportRoot: any;
//   }
// }

function getAnimationCtx() {
  const anim = document.getElementById('animFrame') as HTMLIFrameElement;
  if (anim && anim.contentWindow && anim.contentWindow.exportRoot) {
    return anim.contentWindow.exportRoot;
  }
  return null;
}

function stopWriting() {
  const anim = getAnimationCtx();
  if (anim && anim.currentFrame === 83 && anim.quillbot.currentFrame === 29) {
    anim.quillbot.play();
  } else {
    setTimeout(stopWriting, 200);
  }
}

function startWriting() {
  const anim = getAnimationCtx();
  if (anim && anim.currentFrame === 83 && anim.quillbot.currentFrame === 0) {
    anim.quillbot.play();
  } else {
    setTimeout(startWriting, 200);
  }
}

class FeedbackDrawer extends Component<IProps, IState> {
  isWriting: boolean;

  typingTimer: ReturnType<typeof setTimeout> | number;

  feedbackTextRef: React.RefObject<HTMLTextAreaElement>;

  constructor(props: IProps) {
    super(props);
    this.feedbackTextRef = React.createRef<HTMLTextAreaElement>();
    this.state = {
      feedbackSubmitted: false,
      startedWriting: false,
      showError: false,
    };
    this.isWriting = false;
    this.typingTimer = 0;
  }

  getReady() {
    if (!this.state.startedWriting) {
      const anim = getAnimationCtx();
      setTimeout(this.getReady.bind(this), 200);
      if (anim !== null && anim.currentFrame === 26) {
        anim.play();
        this.setState({ startedWriting: true });
      }
    }
  }

  isTyping = () => {
    if (!this.isWriting) {
      startWriting();
    }
    this.isWriting = true;

    if (this.typingTimer !== null) {
      clearTimeout(this.typingTimer as ReturnType<typeof setTimeout>);
    }

    this.typingTimer = setTimeout(() => {
      if (this.isWriting) {
        stopWriting();
      }
      this.isWriting = false;
    }, 1800);
  };

  sendFeedback = () => {
    const { user } = this.props;
    const textArea = this.feedbackTextRef.current;
    if (textArea) {
      if (textArea?.value === '') {
        this.setState({ showError: true });
        return;
      }
      TrackSupport.sendFeedback(
        user?.fullName || '',
        user?.email || '',
        textArea?.value || '',
        () => {
          textArea.value = '';
          this.setState({ feedbackSubmitted: true });
        }
      );
    }
  };

  renderFeedbackBox = () => {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <Typography style={{ fontWeight: 'bold', paddingTop: '20px' }}>
            How am I doing?
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="body2" gutterBottom>
            Have a suggestion, found some bug, enjoy the product? Let me know.
          </Typography>
        </Grid>
        {this.state.feedbackSubmitted ? (
          <Grid item className="qbp-pb20">
            <Typography
              variant="caption"
              className="qbp-semibold"
              color="primary"
              gutterBottom
            >
              Thank you for your questions and comments. We value your ideas,
              feedback and support.
            </Typography>
          </Grid>
        ) : null}
        <Grid item>
          <textarea
            placeholder={
              this.state.feedbackSubmitted ? 'Add another' : 'Start here'
            }
            onChange={() => {
              this.isTyping();
              this.setState({ showError: false });
              if (this.state.feedbackSubmitted) {
                this.setState({ feedbackSubmitted: false });
              }
            }}
            ref={this.feedbackTextRef}
            data-gramm_editor="false"
            wt-ignore-input="true"
            className={`${classes.textArea} feedback-textarea ${
              this.state.showError ? classes.textAreaError : ''
            }`}
            id="feedbackText"
            onFocus={this.getReady.bind(this)}
          />
        </Grid>
        {this.state.showError && (
          <Grid item style={{ padding: '0px 10px' }}>
            <Typography variant="caption" color="error">
              Required
            </Typography>
          </Grid>
        )}
        <Grid item>
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button
                onClick={() => window.open('https://discord.gg/GBX3f6Y')}
                color="primary"
                style={{ marginRight: '5px' }}
              >
                Use Discord
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={this.sendFeedback}
                variant="contained"
                color="primary"
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  render() {
    return (
      <Grid container style={{ display: 'relative' }}>
        <Grid item xs={12}>
          <Grid container direction="column">
            <Grid item>{this.renderFeedbackBox()}</Grid>
            {!(
              this.props?.common?.platform?.isExtension &&
              this.props?.common?.platform?.extensionMetaData?.type ===
                'ms-word'
            ) && (
              <Grid item>
                <iframe
                  title="quillanim"
                  src={`${publicRuntimeConfig.ASSETS_URL}/mascot/quillbot_feedback_animation/quillAnim.html`}
                  className="anim-frame"
                  id="animFrame"
                  scrolling="no"
                  style={{
                    border: 'none',
                    height: '40vh',
                    position: 'absolute',
                    bottom: 10,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

const mapStateToProps = (store: RootState) => ({
  user: store.user,
  common: store.common,
});

export default connect(mapStateToProps)(withStyles(styles)(FeedbackDrawer));
